import React from 'react';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import { rowsPerPageOptions } from '../../config';
import {
    formatDateWithoutHours,
    momentZoneToDateAddDays,
    momentZoneToUnix,
} from '../../util/dateUtil';
import './styles.scss';
import { IconComponent, icons } from '../../componets/icons';
import { ColumnDef } from '@tanstack/react-table';
import { useAuth } from '../../context/AuthContext';
import { IFormInputPropsFilter } from '../../componets/interfaces/formInputPropsFilter';
import ListComponet from '../../componets/list';
import { ImperativeHandleAccessedByParentListComponent } from '../../componets/interfaces/listProps';
import { IFormSettinsProps } from '../../componets/interfaces/formProps';
import { FormInputProps } from '../../componets/form/formInterfaces';
import { setHours, setMinutes } from 'date-fns';
import { setTypeUserInQuery } from '../../util/configTypeUser';
import { formatNumber } from '../../util/infoFormat';
import { cpfAndCnpjMask } from '../../componets/form/mask/cpfAndCnpj';
import {
    Box,
    Checkbox,
    Grid,
    Paper,
    Stack,
    TablePagination,
    Tooltip,
    Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { ref } from 'yup';
import { defaultLabelDisplayedRows } from '../../communs/tablePaginationAssistant';
import AlertDialog from '../../componets/dialog/alertDialog';
import GlobalFilterSearchComponent from '../../componets/filterComponent/filterSearch';
import { FormButton } from '../../componets/form/formButton';
import { TableGeneric } from '../../componets/table';
import { useContextGlobal } from '../../context/ContextGlobal';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import {
    handleExceptionMessage,
    handleExceptionMultipleMessages,
} from '../../util/handleExceptionAxios';
import { message } from '../../util/handleMessages';
import {
    queryToObj,
    objToQuery,
    handleChangePage,
    handleChangeRowsPerPage,
} from '../../util/query';
import { FormInputText } from '../../componets/form/formInputText';
import RenderForm from '../../componets/form/renderForm';

import {
    GenericEnum,
    yesOrNoOrBothOptions,
} from '../../communs/enums/generic-enum';

interface IItemHonorary {
    originType: {
        id: string;
        description: string;
    };
    description: string;
    quantity: number;
    itemValue: number;
}

interface IInvoiceDetail {
    id: string;
    formPayment: string;
    typePayment: string;
    description: string;
    dueDate: Date;
    installmentValue: number;
    downloaded: boolean;
    legalProcess?: {
        id: string;
        code: string;
        processNumber: string;
        customer: {
            id: string;
            name: string;
            identification: string;
        };
        user: {
            id: string;
            name: string;
        };
        honoraryValue: number;
    };
    additionalHonorarium?: {
        id: string;
        code: string;
        processNumber: string;
        customer: {
            id: string;
            name: string;
            identification: string;
        };
        user: {
            id: string;
            name: string;
        };
        itemHonoraries: IItemHonorary[];
    };
    commissionGeneratedAutomatically: boolean;
    commissionGenerated: boolean;
}

const defaultValues = {
    customer: {
        id: '',
        name: '',
    },
    user: [],
    dateInitial: momentZoneToDateAddDays({ days: -90, startOfType: 'day' }),
    dateFinal: momentZoneToDateAddDays({ days: 0, endOfType: 'day' }),
    processNumber: '',
    code: '',
};

interface IStackComponent {
    mainInputs: FormInputProps[];
    inputs: IFormInputPropsFilter[];
    setToggleSearch: (open: boolean) => void;
    handleClearDates: (keys: string[]) => void;
    inactiveFilterClean?: boolean;
}

const StackComponent = (props: IStackComponent) => {
    const {
        mainInputs,
        inputs,
        setToggleSearch,
        handleClearDates,
        inactiveFilterClean,
    } = props;

    if (mainInputs.length === 0 && inputs[0]?.control) {
        return (
            <FormInputText
                name={inputs[0].name}
                control={inputs[0].control}
                label={inputs[0].label}
                size={inputs[0].size}
                variant={inputs[0].variant}
                handleOnKeyPress={inputs[0].handleOnKeyPress}
            />
        );
    }

    if (mainInputs.length > 0) {
        return (
            <>
                <Grid container spacing={2}>
                    <RenderForm inputsForm={mainInputs} />
                </Grid>
                {!inactiveFilterClean && (
                    <Tooltip
                        title="Limpar filtro"
                        onClick={() =>
                            handleClearDates(['dateInitial', 'dateFinal'])
                        }>
                        <IconButton>
                            <IconComponent icon={icons.ClearAll} />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip
                    title="Opções de filtros"
                    onClick={() => setToggleSearch(true)}>
                    <IconButton data-qa="filter-options">
                        <IconComponent icon={icons.FilterList} />
                    </IconButton>
                </Tooltip>
            </>
        );
    }

    return <></>;
};

const paramsModais = {
    titleConfirm: {
        writeOff: {
            message: 'Deseja dar baixa da comissão?',
        },
        unsubscribe: {
            message: 'Deseja cancelar a baixa da comissão?',
        },
    },
};

const yesOrNoOrBothInstance = new GenericEnum(yesOrNoOrBothOptions);

const ComissionList: React.FC<IFormSettinsProps> = props => {
    const { settings } = props;

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(0);
    const [rows, setRows] = React.useState<IInvoiceDetail[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [toggleSearch, setToggleSearch] = React.useState<boolean>(false);
    const [loadNewData, setLoadNewData] = React.useState<boolean>(false);
    const [openModalAction, setOpenModalAction] = React.useState(false);
    const [messageDialogTitle, setMessageDialogTitle] = React.useState<
        string | undefined
    >(undefined);
    const [messageDialogContent, setMessageDialogContent] = React.useState<
        string | undefined
    >(undefined);

    const history = useHistory();
    const { search } = useLocation();
    const { setOpenLoading } = useContextGlobal();
    const { addToast } = useToast();

    const { control, setValue, clearErrors, setError } = useForm<any>({
        defaultValues,
    });

    React.useEffect(() => {
        let queryT = '';

        if (window.location.search && window.location.search.length > 0) {
            queryT = window.location.search.replace('?', '');
        }
        const queryToObjParams = queryToObj(queryT);
        if (queryToObjParams?.action && queryToObjParams?.invoiceId) {
            loadInvoice({
                id: queryToObjParams.invoiceId,
                action: queryToObjParams.action,
            });
        }
    }, [window.location.search]);

    const loadInvoice = async ({
        id,
        action,
    }: {
        id: string;
        action: 'unsubscribe' | 'writeOff';
    }): Promise<void> => {
        setOpenLoading(true);

        try {
            const response = await api.get(`invoice-detail/${id}`);
            const data = response.data;

            const operationInfo = plotDescription(data);

            setMessageDialogContent(`
                                    <p style="margin: 0 0 8px 0;"><strong>Descrição da fatura:</strong> ${response.data.description}</p>
                                    <strong>Origem</strong>: ${operationInfo.description} ${operationInfo.code}`);

            setMessageDialogTitle(paramsModais.titleConfirm[action].message);
            setOpenModalAction(true);
            setOpenLoading(false);
        } catch (error) {
            addToast({
                type: 'error',
                title: message.error.selectOne,
                description: '',
            });
            setOpenLoading(false);
        }
    };

    const plotDescription = (data: any) => {
        const operationInfo: any = {
            code: '',
            description: '',
        };

        if (data?.legalProcess) {
            operationInfo.code = data.legalProcess.processNumber;
            // operationInfo.description = 'Processo';
            operationInfo.description = '';
        }

        if (data?.additionalHonorarium) {
            const origins = data.additionalHonorarium.itemHonoraries.map(
                (item: any) => item.originType.description,
            );
            operationInfo.code = origins.join(', ');
            // operationInfo.description = 'Honorários de';
            operationInfo.description = '';
        }

        if (data?.additionalExpense) {
            const origins = data.additionalExpense.itemExpenses.map(
                (item: any) => item.originExpenditure.description,
            );
            operationInfo.code = origins.join(', ');
            // operationInfo.description = 'Despesas de';
            operationInfo.description = '';
        }

        return operationInfo;
    };
    const handleCodeOrProcessNumber = React.useCallback(
        (original: IInvoiceDetail) => {
            if (original?.additionalHonorarium?.processNumber) {
                return original.additionalHonorarium.processNumber;
            }
            if (original?.additionalHonorarium?.code) {
                return original.additionalHonorarium.code;
            }
            if (original?.legalProcess?.code) {
                return original.legalProcess.code;
            }
            if (original?.legalProcess?.processNumber) {
                return original.legalProcess.processNumber;
            }
            return '';
        },
        [],
    );

    const handleOrigin = React.useCallback((original: IInvoiceDetail) => {
        const dataPlot = plotDescription(original);
        if (dataPlot?.description) {
            const { code, description } = dataPlot;
            return `${description} ${code.toLowerCase()}`;
        }
        if (dataPlot?.code) {
            const { code } = dataPlot;
            return code;
        }
        return '';
    }, []);

    const handleUser = React.useCallback((original: IInvoiceDetail) => {
        if (original?.legalProcess?.user) {
            return original.legalProcess.user.name;
        }
        if (original?.additionalHonorarium?.user) {
            return original.additionalHonorarium.user.name;
        }
        return '';
    }, []);

    const handleCustomer = (original: IInvoiceDetail) => {
        if (original?.legalProcess?.customer) {
            return original.legalProcess.customer.name;
        }
        if (original?.additionalHonorarium?.customer) {
            return original.additionalHonorarium.customer.name;
        }
        return '';
    };

    const handleIdentification = (original: IInvoiceDetail) => {
        if (original?.legalProcess?.customer) {
            return cpfAndCnpjMask(
                original.legalProcess?.customer?.identification,
                false,
            );
        }
        if (original?.additionalHonorarium?.customer) {
            return cpfAndCnpjMask(
                original.additionalHonorarium?.customer?.identification,
                false,
            );
        }
        return '';
    };

    const columnsTable = React.useMemo<ColumnDef<Object>[]>(
        () => [
            {
                accessorKey: 'commissionGeneratedAutomatically',
                cell: (cell: any) => (
                    <TableCell align="left">
                        <Checkbox
                            checked={
                                rows[cell.row.index]
                                    .commissionGeneratedAutomatically
                            }
                            onChange={event => {
                                console.log(cell.getValue());
                                let index = cell.row.index;
                                let rowsData = rows;
                                if (Array.isArray(rows)) {
                                    rowsData[
                                        index
                                    ].commissionGeneratedAutomatically =
                                        event.target.checked;
                                    setRows([...rowsData]);
                                }
                            }}
                            disabled={rows[cell.row.index].commissionGenerated}
                        />
                    </TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Dar baixa
                    </TableCell>
                ),
            },
            {
                accessorKey: 'dueDate',
                cell: (cell: any) => (
                    <TableCell align="left">
                        {formatDateWithoutHours(cell?.getValue())}
                    </TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Data vencimento
                    </TableCell>
                ),
            },
            {
                accessorKey: 'paymentDate',
                cell: (cell: any) => (
                    <TableCell align="left">
                        {cell?.getValue()
                            ? formatDateWithoutHours(cell?.getValue())
                            : ''}
                    </TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Data pagamento
                    </TableCell>
                ),
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Código / Número
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell align="left" key={row.id}>
                            {handleCodeOrProcessNumber(row.original)}
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'description',
                cell: (cell: any) => (
                    <TableCell align="left">{cell?.getValue()}</TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Descrição
                    </TableCell>
                ),
            },
            {
                id: 'select',
                header: (info: any) => {
                    return (
                        <TableCell align={'left'} padding={'normal'}>
                            Cliente
                        </TableCell>
                    );
                },
                cell: ({ row }: any) => {
                    return (
                        <TableCell align="left" key={row.id}>
                            {handleCustomer(row.original)}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Identificação
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell key={row.id} align="left">
                            {handleIdentification(row.original)}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Origem
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell key={row.id} align="left">
                            {handleOrigin(row.original)}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Advogado
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell key={row.id} align="left">
                            {handleUser(row.original)}
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'installmentValue',
                cell: (cell: any) => (
                    <TableCell align="left">
                        {formatNumber(cell?.getValue())}
                    </TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Valor
                    </TableCell>
                ),
            },
            {
                id: 'select',
                header: ({ table }: any) => <TableCell align="center" />,
                cell: ({ row }: any) => (
                    <TableCell align="right">
                        <div className="tooltip-actions">
                            <Tooltip
                                title="Dar baixa"
                                onClick={() => {
                                    const objQuery = queryToObj(
                                        window.location.search.replace('?', ''),
                                    );
                                    const action = 'writeOff';
                                    objQuery.action = action;
                                    objQuery.invoiceId = row.original.id;

                                    history.push(`?${objToQuery(objQuery)}`);
                                }}>
                                <IconButton
                                    disabled={row.original.commissionGenerated}
                                    data-qa="discharge">
                                    <IconComponent icon={icons.DownloadDone} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip
                                title="Cancelar baixa"
                                onClick={() => {
                                    const objQuery = queryToObj(
                                        window.location.search.replace('?', ''),
                                    );
                                    const action = 'unsubscribe';
                                    objQuery.action = action;
                                    objQuery.invoiceId = row.original.id;

                                    history.push(`?${objToQuery(objQuery)}`);
                                }}>
                                <IconButton
                                    disabled={!row.original.commissionGenerated}
                                    data-qa="cancel-download">
                                    <IconComponent icon={icons.NotInterested} />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </TableCell>
                ),
            },
        ],
        [handleCodeOrProcessNumber, handleOrigin, handleUser, history, rows],
    );

    const { user } = useAuth();

    const QUERY_DEFAULT = `page=1&limit=${
        rowsPerPageOptions[0]
    }&dateInitial=${momentZoneToUnix(
        defaultValues.dateInitial,
    )}&dateFinal=${momentZoneToUnix(defaultValues.dateFinal)}&user=${user.sub}`;

    const inputsFilter = React.useMemo<IFormInputPropsFilter[]>(
        () => [
            {
                typeInput: 'text',
                name: `processNumber`,
                control: null,
                label: 'Número processo',
                setValue: null,
                size: 'small',
                variant: 'standard',
                handleOnKeyPress: (data: any, name?: string) => {
                    if (data.charCode === 13 && name) {
                        handleByOnKeyPressEnterNewQuery(
                            data.target.value,
                            name,
                        );
                    }
                },
            },
            {
                typeInput: 'autocomplete',
                name: `customer.id`,
                variant: 'standard',
                control: null,
                label: 'Cliente',
                loadingAutocomplete: false,
                setValue: null,
                handleChange: (event: any, option: any, name: string) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        handleByOnKeyPressEnterNewQuery(option?.id, name);
                    }
                },
                activeDebounce: true,
                query: (value: string) => `customer?name=${value}`,
            },
            {
                typeInput: 'multiple',
                name: `user`,
                variant: 'standard',
                control: null,
                label: 'Advogado',
                handleChange: (event: any, option: any[]) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        const data = option?.map(o => o.id);
                        handleByOnKeyPressEnterNewQuery(
                            data.length > 0 ? data : null,
                            'user',
                        );
                    }
                },
                setValue: null,
                entityPath: 'users',
                keyQuery: 'user.id',
                activeDebounce: true,
                query: (value: string) =>
                    `users?${setTypeUserInQuery(user.typeUser)}&limit=${value}`,
                DAODataList: (value: any) => {
                    return {
                        id: value.id,
                        description: value.name,
                    };
                },
            },
            {
                typeInput: 'autocomplete',
                name: 'invoiceDetail.commissionGenerated.numberToBoolean',
                variant: 'standard',
                control: null,
                label: 'Comissão baixada',
                loadingAutocomplete: false,
                setValue: null,
                options: yesOrNoOrBothInstance.optionsList(),
                activeDebounce: false,
                handleChange: (event: any, option: any, name: string) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        handleByOnKeyPressEnterNewQuery(option?.id, name);
                    }
                },
            },
            {
                typeInput: 'text',
                name: `code`,
                control: null,
                label: 'Código',
                setValue: null,
                size: 'small',
                variant: 'standard',
                handleOnKeyPress: (data: any, name?: string) => {
                    if (data.charCode === 13 && name) {
                        handleByOnKeyPressEnterNewQuery(
                            data.target.value,
                            name,
                        );
                    }
                },
            },
        ],
        [],
    );

    const mainInputsFilter = React.useMemo<FormInputProps[]>(
        () => [
            {
                typeInput: 'dateHour',
                name: 'dateInitial',
                control: null,
                label: 'Date de início',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 0);
                    newTime = setMinutes(newTime, 1);
                    handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateInitial',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
            {
                typeInput: 'dateHour',
                name: 'dateFinal',
                control: null,
                label: 'Date de fim',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 23);
                    newTime = setMinutes(newTime, 59);
                    handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateFinal',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
        ],
        [],
    );

    const inputs = React.useMemo<IFormInputPropsFilter[]>(
        () => [
            ...inputsFilter.map(i => ({
                ...i,
                setValue,
                control,
            })),
        ],
        [inputsFilter, setValue, control],
    );

    const mainInputs = React.useMemo<FormInputProps<any>[]>(
        () => [
            ...mainInputsFilter.map(i => ({
                ...i,
                setValue,
                control,
                setError,
                clearErrors,
            })),
        ],
        [mainInputsFilter, setValue, control, setError, clearErrors],
    );

    const loadRows = React.useCallback(
        async (queryString: string): Promise<void> => {
            setOpenLoading(true);
            try {
                let apiPath = settings.apiPath;
                const response = await api.get(`${apiPath}?${queryString}`);
                setRows(response.data.data);
                setTotal(response.data.count);
                setOpenLoading(false);
                if (loadNewData) {
                    setLoadNewData(false);
                }
            } catch (error) {
                const { messageResponse, typeFeedback } = handleExceptionMessage(error);
                addToast({
                    type: typeFeedback,
                    title: message.error.selectAll,
                    description: messageResponse,
                });
                setOpenLoading(false);
            }
        },
        [addToast, loadNewData, setOpenLoading, settings],
    );

    // HANDLER LOAD NEW QUERY FILTER
    const handleByOnKeyPressEnterNewQuery = React.useCallback(
        (value: any, name: string) => {
            const queryObj = queryToObj(search.replace('?', ''));

            const newQuery = {
                ...queryObj,
                [name]: value,
                page: 1,
            };
            const nQuery = objToQuery(newQuery);
            history.push(`?${nQuery}`);
            loadRows(nQuery);

            if (window.screen.width < 900) {
                setToggleSearch(false);
            }
        },
        [history, search, loadRows],
    );

    const handleClearDates = React.useCallback(
        (keys: string[]) => {
            let queryT = search.replace('?', '');
            const objQuery = queryToObj(queryT);

            setTimeout(() => {
                keys.forEach((key: any) => {
                    setValue(key, null);
                    setValue(key, null);
                });
            }, 200);

            delete objQuery.dateInitial;
            delete objQuery.dateFinal;

            queryT = objToQuery(objQuery);
            history.push(`?${queryT}`);
            setLoadNewData(true);
        },
        [history, search, setValue],
    );

    const handleChecked = () => {
        const size =
            rows.filter(i => i.commissionGeneratedAutomatically)?.length || 0;
        return (rows.length as number) === size;
    };

    const handleLabelCommissionGenerated = () => {
        const size =
            rows.filter(i => i.commissionGeneratedAutomatically)?.length || 0;

        if (size > 1) {
            return 'Dar baixa em comissões';
        }
        return 'Dar baixa em comissão';
    };

    const handleRunMultipleLines = async () => {
        const lines = rows.filter(i => i.commissionGeneratedAutomatically);

        if (lines.length === 0) {
            addToast({
                type: 'warn',
                title: 'Nenhum registro foi selecionado',
                description: '',
            });
            return;
        }

        try {
            await api.patch(`${settings.apiPath}/action/writeOff`, {
                ids: lines.map(l => l.id),
            });

            setLoadNewData(true);
            addToast({
                type: 'success',
                title: message.success.saveAllOrOne,
                description: '',
            });
        } catch (error) {
            addToast({
                type: 'error',
                title: message.error.saveAllOrOne,
                description: '',
            });
        }
    };

    const handleRunLow = async ({
        id,
        action,
    }: {
        id: string;
        action: 'unsubscribe' | 'writeOff';
    }) => {
        try {
            setOpenLoading(true);
            await api.patch(`${settings.apiPath}/action/${action}`, {
                ids: [id],
            });
            addToast({
                type: 'success',
                title: message.success.save,
                description: '',
            });
            setOpenLoading(false);
            setLoadNewData(true);
        } catch (error) {
            setOpenLoading(false);
            const messagesResponse = handleExceptionMultipleMessages(error);
            for (const messageResponse of messagesResponse) {
                addToast({
                    type: 'error',
                    title: message.error.save,
                    description: messageResponse,
                });
            }
        }
    };

    // Removendo parâmetro modalAction
    const parametersOfQuery = (queryString: string) => {
        const queryUseInFind = queryToObj(queryString);
        if (queryUseInFind.action && queryUseInFind.invoiceId) {
            delete queryUseInFind.action;
            delete queryUseInFind.invoiceId;
        }
        const queryStringUseInFind = objToQuery(queryUseInFind);
        return queryStringUseInFind;
    };

    const handleConfirmAction = async (confirm: boolean) => {
        if (confirm) {
            setOpenModalAction(false);
            let queruStr =
                window.location.search?.length > 0
                    ? window.location.search.replace('?', '')
                    : '';
            const queryObj = queryToObj(queruStr);
            handleRunLow({ id: queryObj.invoiceId, action: queryObj.action });
        } else {
            setOpenModalAction(false);
        }

        const queryStringUseInFind = parametersOfQuery(
            window.location.search.replace('?', ''),
        );
        history.push(`?${queryStringUseInFind}`);
    };

    const handleCloseModalAction = () => {
        setOpenModalAction(false);
        const queryStringUseInFind = parametersOfQuery(
            window.location.search.replace('?', ''),
        );
        history.push(`?${queryStringUseInFind}`);
    };

    return (
        <div className="principal-container-commission">
            <AlertDialog
                handleConfirmation={handleConfirmAction}
                open={openModalAction}
                messageDialogTitle={messageDialogTitle}
                handleCloseProps={handleCloseModalAction}
                messageDetail={messageDialogContent}
            />
            <Box className={toggleSearch ? 'container-box' : ''}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <div className="header-list">
                        <div className="header-list-title">
                            <Typography
                                sx={{ flex: '1 1 100%' }}
                                variant="h6"
                                id="tableTitle"
                                component="div">
                                {settings.title}
                            </Typography>
                        </div>
                        <div className="header-list-search">
                            <Stack
                                spacing={0.5}
                                direction="row"
                                width={'100%'}
                                justifyContent="space-between"
                                alignItems="flex-end">
                                <StackComponent
                                    mainInputs={mainInputs}
                                    inputs={inputs}
                                    setToggleSearch={setToggleSearch}
                                    handleClearDates={handleClearDates}
                                    inactiveFilterClean={false}
                                />
                            </Stack>
                        </div>
                    </div>
                    <Typography
                        sx={{
                            pl: { xs: 1, sm: 2 },
                            pr: { xs: 1, sm: 1 },
                            fontSize: '0.875rem',
                        }}>
                        Selecionar todas:{' '}
                        <Checkbox
                            checked={handleChecked()}
                            onChange={event => {
                                let rowsData = rows;
                                rowsData.forEach(p => {
                                    if (p.commissionGenerated) {
                                        return;
                                    }
                                    p.commissionGeneratedAutomatically =
                                        event.target.checked;
                                });
                                setRows([...rowsData]);
                            }}
                            disabled={!rows.some(i => !i.commissionGenerated)}
                        />
                    </Typography>
                    <TableGeneric
                        data={rows}
                        columns={columnsTable}
                        minWidth={650}
                    />
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={total}
                        labelRowsPerPage={'Linhas por página'}
                        labelDisplayedRows={defaultLabelDisplayedRows}
                        rowsPerPage={rowsPerPage}
                        page={total > 0 ? page : 0}
                        onPageChange={(event: unknown, newPage: number) =>
                            handleChangePage(
                                event,
                                newPage,
                                setPage,
                                history,
                                loadRows,
                            )
                        }
                        onRowsPerPageChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) =>
                            handleChangeRowsPerPage(
                                event,
                                setRowsPerPage,
                                setPage,
                                history,
                                loadRows,
                            )
                        }
                    />
                    <Typography
                        sx={{
                            pl: { xs: 1, sm: 2 },
                            pb: { xs: 1, sm: 2 },
                        }}>
                        <FormButton
                            variant="contained"
                            icon={<IconComponent icon={icons.Save} />}
                            label={handleLabelCommissionGenerated()}
                            typeButton="custom"
                            onClick={() => handleRunMultipleLines()}
                        />
                    </Typography>
                </Paper>
            </Box>

            <GlobalFilterSearchComponent
                inputs={inputs}
                setToggleSearch={setToggleSearch}
                toggleSearch={toggleSearch}
                setRowsPerPage={setRowsPerPage}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                page={page}
                implementationLoadRows={loadRows}
                updateRows={loadNewData}
                setUpdateRows={setLoadNewData}
                queryDefault={QUERY_DEFAULT}
            />
        </div>
    );
};

export default ComissionList;
